/**
 * The schedule contract as the panels read it.
 *
 * WP1-SCHED-API owns `packages/contracts/src/schedule.ts`; everything it
 * publishes is re-exported from here so the module has one import path. The
 * panels' own proposal (provider goals on the day) follows below; see
 * docs/requests/sched-panels-api-endpoints.md.
 */
import { IdSchema } from '@velora/contracts/sync';
import {
  CompoundMoveResultSchema,
  MoveResultSchema,
  ScheduleContextSchema,
  ScheduleDaySchema,
  type MoveResult,
} from '@velora/contracts/schedule';
import { z } from 'zod';

export * from '@velora/contracts/schedule';

/** `MoveResult` carries the server-side undo entry (`actionId`) in the published contract. */
export const MoveResultWithActionSchema = MoveResultSchema;
export type MoveResultWithAction = MoveResult;
export const CompoundMoveResultWithActionSchema = CompoundMoveResultSchema;

/* ======================================================================== */
/* Panels proposal (docs/requests/sched-panels-api-endpoints.md)             */
/* ======================================================================== */

/** Production goal of one provider on one date, already prorated for time off. */
export const ProviderGoalSchema = z.object({
  providerId: IdSchema,
  goalCents: z.number().int().nonnegative(),
});
export type ProviderGoal = z.infer<typeof ProviderGoalSchema>;

/**
 * `ScheduleContext` as the panels read it: `days[].providerGoals` is the
 * proposed addition (null without `money.view`, absent until WP1-SCHED-API
 * adds it, in which case the huddle shows production without a goal).
 */
export const PanelScheduleDaySchema = ScheduleDaySchema.extend({
  providerGoals: z.array(ProviderGoalSchema).nullable().optional(),
});
export type PanelScheduleDay = z.infer<typeof PanelScheduleDaySchema>;

export const PanelScheduleContextSchema = ScheduleContextSchema.extend({
  days: z.array(PanelScheduleDaySchema),
});
export type PanelScheduleContext = z.infer<typeof PanelScheduleContextSchema>;
